import React, { useEffect, useMemo, useRef, useState } from "react"
import * as Icons from "lucide-react"

// Break the message into sentence-ish chunks so Prev/Next can skip through it.
function splitChunks(text) {
  const clean = String(text || "")
    .replace(/\s+/g, " ")
    .trim()
  if (!clean) return []
  const parts = clean.match(/[^.!?]+[.!?]*/g) || [clean]
  const chunks = []
  let buf = ""
  for (const p of parts) {
    buf += p
    if (buf.trim().length > 160 || /[.!?]$/.test(buf.trim())) {
      chunks.push(buf.trim())
      buf = ""
    }
  }
  if (buf.trim()) chunks.push(buf.trim())
  return chunks.length ? chunks : [clean]
}

export default function EmailReadAloud({ text, onClose }) {
  const synth = typeof window !== "undefined" ? window.speechSynthesis : null
  const chunks = useMemo(() => splitChunks(text), [text])
  const [voices, setVoices] = useState([])
  const [voiceURI, setVoiceURI] = useState("")
  const [rate, setRate] = useState(1)
  const [playing, setPlaying] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const idxRef = useRef(0)
  const rateRef = useRef(1)
  const voiceRef = useRef("")

  useEffect(() => {
    rateRef.current = rate
  }, [rate])
  useEffect(() => {
    voiceRef.current = voiceURI
  }, [voiceURI])

  // Load the browser's available voices (may arrive asynchronously).
  useEffect(() => {
    if (!synth) return undefined
    const load = () => {
      const v = synth.getVoices()
      if (v && v.length) {
        setVoices(v)
        setVoiceURI(prev => prev || (v.find(x => x.default) || v[0]).voiceURI)
      }
    }
    load()
    synth.addEventListener && synth.addEventListener("voiceschanged", load)
    return () => {
      synth.removeEventListener &&
        synth.removeEventListener("voiceschanged", load)
    }
  }, [synth])

  const speakFrom = i => {
    if (!synth) return
    synth.cancel()
    if (i < 0 || i >= chunks.length) {
      setPlaying(false)
      return
    }
    idxRef.current = i
    const u = new window.SpeechSynthesisUtterance(chunks[i])
    u.rate = rateRef.current
    const v = voices.find(x => x.voiceURI === voiceRef.current)
    if (v) u.voice = v
    u.onend = () => {
      if (idxRef.current + 1 < chunks.length) speakFrom(idxRef.current + 1)
      else setPlaying(false)
    }
    synth.speak(u)
    setPlaying(true)
  }

  // Start reading on open; stop on close/unmount.
  useEffect(() => {
    if (chunks.length) speakFrom(0)
    return () => {
      if (synth) synth.cancel()
    }
  }, [chunks])

  const togglePlay = () => {
    if (!synth) return
    if (synth.speaking && !synth.paused) {
      synth.pause()
      setPlaying(false)
    } else if (synth.paused) {
      synth.resume()
      setPlaying(true)
    } else speakFrom(idxRef.current)
  }
  const prev = () => speakFrom(Math.max(0, idxRef.current - 1))
  const next = () => speakFrom(Math.min(chunks.length - 1, idxRef.current + 1))

  const applyRate = r => {
    rateRef.current = r
    setRate(r)
    if (synth && synth.speaking) speakFrom(idxRef.current)
  }
  const applyVoice = uri => {
    voiceRef.current = uri
    setVoiceURI(uri)
    if (synth && synth.speaking) speakFrom(idxRef.current)
  }
  const stop = () => {
    if (synth) synth.cancel()
    onClose && onClose()
  }

  if (!synth) return null

  return (
    <div className="em-ra">
      <div className="em-ra-bar">
        <button className="em-ra-btn" title="Previous" onClick={prev}>
          <Icons.SkipBack size={18} />
        </button>
        <button
          className="em-ra-btn em-ra-play"
          title={playing ? "Pause" : "Play"}
          onClick={togglePlay}
        >
          {playing ? <Icons.Pause size={18} /> : <Icons.Play size={18} />}
        </button>
        <button className="em-ra-btn" title="Next" onClick={next}>
          <Icons.SkipForward size={18} />
        </button>
        <button
          className={`em-ra-btn ${showSettings ? "em-ra-btn-active" : ""}`}
          title="Voice settings"
          onClick={() => setShowSettings(s => !s)}
        >
          <Icons.Settings size={17} />
        </button>
        <button
          className="em-ra-btn em-ra-close"
          title="Stop reading"
          onClick={stop}
        >
          <Icons.X size={18} />
        </button>
      </div>

      {showSettings && (
        <div className="em-ra-panel">
          <div className="em-ra-field">
            <label>Reading speed</label>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={rate}
              onChange={e => applyRate(parseFloat(e.target.value))}
            />
            <span className="em-ra-rate">{rate.toFixed(1)}x</span>
          </div>
          <div className="em-ra-field">
            <label>Voice Selection</label>
            <select value={voiceURI} onChange={e => applyVoice(e.target.value)}>
              {voices.length === 0 && <option>Default</option>}
              {voices.map(v => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name}
                  {v.lang ? ` (${v.lang})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  )
}
